import { useSelector } from "react-redux";
import {
  normalizeRoles,
  primaryRole as rankPrimary,
  hasAnyRole,
  deriveCompliance,
} from "../utils/complianceRoles";

/**
 * useRole — reads the logged-in admin user's roles and permission categories
 * from Redux (falls back to localStorage so it survives reloads).
 *
 * The login response carries:
 *   data.roles                 — every active role code, e.g. ['AD','CCO']
 *   data.primary_role          — the ranked primary (server side)
 *   data.role_name             — the ranked primary, kept for compatibility
 *   data.permission_categories — distinct sectionName values, merged across roles
 *   data.AdminRole             — legacy object { role, name } (backwards-compat)
 *
 * A cached pre-multi-role session has no `roles`, so normalizeRoles rebuilds a
 * one-element list from role_name / AdminRole and every predicate below keeps
 * working until that session cycles.
 *
 * Consumers should prefer this hook over reading the Redux tree directly so
 * the access pattern stays a single concern and can evolve (e.g. if we later
 * move off Redux or add a second claim shape).
 */
export function useRole() {
  const adminLogin = useSelector((state) => state.adminLogin);

  const fromRedux = adminLogin?.user?.data || null;
  const fromStorage = (() => {
    try {
      return JSON.parse(localStorage.getItem("admin"))?.data || null;
    } catch (_) {
      return null;
    }
  })();
  const src = fromRedux || fromStorage || {};

  const roles = normalizeRoles(src);
  const primary = src.role_name || rankPrimary(roles);

  const permissionCategories = Array.isArray(src.permission_categories)
    ? src.permission_categories
    : [];

  const isAuthed = Boolean(src.id);
  const compliance = deriveCompliance(roles);

  return {
    /** every active role code held by this session, e.g. ['AD','CCO'] */
    roles,
    /** the highest-ranked code held, or null */
    primaryRole: primary,
    /** alias of primaryRole — the many call sites reading roleName still work */
    roleName: primary,
    /** array of sectionName strings granted to this user's roles */
    permissionCategories,
    /**
     * true iff any held role matches any of the supplied names. Super Admin is
     * a legacy all-admin role that mirrors the server-side wildcard in
     * authJWT.js.requireRole; keep this in sync until every pre-multi-role
     * session has cycled and the wildcard is retired. The wildcard lives here
     * and not in hasAnyRole so it can never widen canDecide / canReview.
     */
    hasRole: (...names) => compliance.isSuperAdmin || hasAnyRole(roles, ...names),
    /** true iff permission_categories includes the supplied sectionName */
    hasCategory: (section) => permissionCategories.includes(section),
    /** true iff a user is logged in at all */
    isAuthed,
    /** canDecide / canReview / isAD / isOversight / isSuperAdmin */
    ...compliance,
  };
}

/**
 * Convenience wrapper — returns just a boolean. Useful in JSX predicates:
 *   { useHasCategory('BDC') && <BdcMenu /> }
 */
export function useHasCategory(section) {
  return useRole().hasCategory(section);
}

/**
 * Convenience wrapper — returns just a boolean:
 *   { useHasRole('AD', 'DR') && <Escalation /> }
 */
export function useHasRole(...names) {
  return useRole().hasRole(...names);
}
